import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { Lab } from "@/lib/types";

const presets = [
  { name: "TSH", unit: "µIU/ml", low: 0.4, high: 4.2 },
  { name: "Free T4", unit: "ng/dL", low: 0.78, high: 2.19 },
  { name: "Free T3", unit: "pg/mL", low: 2.77, high: 5.27 },
  { name: "Total Testosterone", unit: "ng/dL", low: 264, high: 916 },
  { name: "Free Testosterone", unit: "pg/mL", low: 47, high: 244 },
  { name: "SHBG", unit: "nmol/L", low: 10, high: 57 },
  { name: "LH", unit: "mIU/mL", low: 1.7, high: 8.6 },
  { name: "Estradiol", unit: "pg/mL", low: 7.6, high: 42.6 },
  { name: "Prolactin", unit: "ng/mL", low: 4, high: 15.2 },
  { name: "Cortisol (AM)", unit: "µg/dL", low: 6.2, high: 19.4 },
  { name: "HbA1c", unit: "%", low: 4, high: 5.6 },
  { name: "Fasting Insulin", unit: "µIU/mL", low: 2.6, high: 24.9 },
  { name: "Fasting Glucose", unit: "mg/dL", low: 70, high: 99 },
  { name: "B12", unit: "pg/mL", low: 239, high: 931 },
  { name: "Vitamin D", unit: "ng/ml", low: 30, high: 100 },
  { name: "Ferritin", unit: "ng/ml", low: 30, high: 400 },
  { name: "hs-CRP", unit: "mg/L", low: 0, high: 1 },
  { name: "Homocysteine", unit: "µmol/L", low: 4, high: 15 },
];

export function Labs() {
  const [rows, setRows] = useState<Lab[]>([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [test, setTest] = useState(presets[0].name);
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState(presets[0].unit);
  const [low, setLow] = useState<number>(presets[0].low);
  const [high, setHigh] = useState<number>(presets[0].high);
  const [saving, setSaving] = useState(false);

  async function load() {
    const list = await api.labsList();
    list.sort((a, b) => b.test_date.localeCompare(a.test_date));
    setRows(list);
  }
  useEffect(() => { load(); }, []);

  function pickPreset(name: string) {
    setTest(name);
    const p = presets.find((x) => x.name === name);
    if (p) { setUnit(p.unit); setLow(p.low); setHigh(p.high); }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.labCreate({
        test_date: date, test_name: test,
        value: value ? Number(value) : null,
        unit, ref_low: low, ref_high: high, notes: null,
      });
      setValue("");
      await load();
    } catch (err) { alert(String(err)); } finally { setSaving(false); }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Labs</h1>
        <p className="text-sm text-muted">Quarterly full panel. One row per test.</p>
      </div>

      <form onSubmit={submit} className="card space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="label">Date</label>
            <input type="date" className="field" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className="label">Test</label>
            <select className="field" value={test} onChange={(e) => pickPreset(e.target.value)}>
              {presets.map((p) => <option key={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Value</label>
            <input type="number" step="0.01" inputMode="decimal" className="field"
              value={value} onChange={(e) => setValue(e.target.value)} required />
          </div>
          <div>
            <label className="label">Unit</label>
            <input className="field" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </div>
          <div>
            <label className="label">Ref low</label>
            <input type="number" step="0.01" className="field" value={low}
              onChange={(e) => setLow(Number(e.target.value))} />
          </div>
          <div>
            <label className="label">Ref high</label>
            <input type="number" step="0.01" className="field" value={high}
              onChange={(e) => setHigh(Number(e.target.value))} />
          </div>
        </div>
        <button className="btn-primary" disabled={saving}>{saving ? "…" : "Add lab"}</button>
      </form>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm font-mono">
          <thead className="text-left text-muted">
            <tr>
              <th className="py-2">Date</th>
              <th>Test</th>
              <th className="text-right">Value</th>
              <th>Unit</th>
              <th>Range</th>
              <th>Flag</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const low = r.ref_low ?? -Infinity;
              const high = r.ref_high ?? Infinity;
              const v = r.value ?? 0;
              const flag =
                r.value === null ? "" :
                v < low || v > high ? "🔴" :
                v < low * 1.1 || v > high * 0.9 ? "🟡" : "🟢";
              return (
                <tr key={r.id} className="border-t border-border">
                  <td className="py-1 text-muted">{r.test_date}</td>
                  <td>{r.test_name}</td>
                  <td className="text-right">{r.value ?? "—"}</td>
                  <td>{r.unit ?? ""}</td>
                  <td className="text-muted">{r.ref_low ?? "?"}–{r.ref_high ?? "?"}</td>
                  <td>{flag}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
